import styled from 'styled-components'
import { useSelector, useDispatch, } from 'react-redux'
import { useParams, useLocation, useNavigate, } from 'react-router-dom'
import React, { useRef, useMemo, useEffect, useCallback, } from 'react'

import {
  Header, AgentList,
  AgentListMap, DashboardUtils, AgentDetail,
  ControlRoomRequestingSidebar,
} from '@systems'

import { RootState, getAgents, searchAgents, loadMoreAgents, loadMoreSearchAgents, } from '@reducers'

import { AGENT_LIST_PAGE_SIZE, AGENT_POLLING_INTERVAL_MS, } from '@globals'

import { Agent, } from '@types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  .map-container {
    display: flex;
    flex: 1;

    #map {
      width: 100%;
      flex: 1;
    }

    ${ AgentList } {
      position: absolute;
      width: 350px;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      background-color: rgba(255, 255, 255, 0.96);
      align-self: center;
      border-radius: 5px;
      z-index: 100;
      border-radius: 4px;
      overflow: hidden;
    }

    ${ DashboardUtils } {
      position: absolute;
      width: 350px;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      height: 50px;
      background-color: rgba(255, 255, 255, 0.85);
      align-self: center;
      border-radius: 5px;
      z-index: 100;
      border-radius: 4px;
      overflow: hidden;
      top: 100px;
      left: 40px;
    }

    ${ AgentDetail } {
      position: absolute;
      width: 300px;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      background-color: rgba(255, 255, 255, 0.85);
      align-self: center;
      border-radius: 5px;
      z-index: 100;
      border-radius: 4px;
      overflow: hidden;
      top: 160px;
      left: 420px;
    }

    ${ AgentList } {
      height: calc(95vh - 160px);
      top: 160px;
      left: 40px;
    }

    ${ ControlRoomRequestingSidebar } {
      position: absolute;
      top: 100px;
      right: 40px;
      z-index: 100;
    }
  }
`

function MainControllMap() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { agentId: selectedAgentId, } = useParams()
  const defaultAgentPagination = useMemo(
    () => ({
      count: 0,
      next: '',
      previous: '',
    }),
    []
  )

  const agents = useSelector((state: RootState) => state.userManagement.agents)
  const agentPagination = useSelector(
    (state: RootState) => state.userManagement.agentPagination || defaultAgentPagination
  )
  const includeEndOfWorkingAgents = useSelector((state: RootState) => state.appState.data.includeEndOfWorkingAgents)

  const map = useRef<naver.maps.Map>()
  const agentCountRef = useRef(0)

  const agentSearchValue = useMemo(
    () => {
      const urlParams = new URLSearchParams(location.search)
      return urlParams.get('search-agent')
    },
    [ location, ]
  )

  const fetchAgents = useCallback(
    (append = false, offset = 0, limit = AGENT_LIST_PAGE_SIZE) => {
      if(agentSearchValue) {
        dispatch(searchAgents({
          offset,
          limit,
        }, agentSearchValue, append))
      } else {
        dispatch(getAgents({
          offset,
          limit,
        }, includeEndOfWorkingAgents, append))
      }
    },
    [ agentSearchValue, includeEndOfWorkingAgents, ]
  )

  useEffect(
    () => {
      agentCountRef.current = agents.length
    },
    [ agents.length, ]
  )

  useEffect(
    () => {
      fetchAgents()
    },
    [ fetchAgents, ]
  )

  useEffect(
    () => {
      const autoRefreshAgentsInterval = setInterval(
        () => fetchAgents(false, 0, Math.max(agentCountRef.current || 0, AGENT_LIST_PAGE_SIZE)),
        AGENT_POLLING_INTERVAL_MS
      )

      return () => {
        clearInterval(autoRefreshAgentsInterval)
      }
    },
    [ fetchAgents, ]
  )

  const onAgentsEndReached = useCallback(
    () => {
      if(!agentPagination.next) {
        return
      }

      if(agentSearchValue) {
        dispatch(loadMoreSearchAgents({
          offset: agents.length,
          limit: AGENT_LIST_PAGE_SIZE,
        }, agentSearchValue))
      } else {
        dispatch(loadMoreAgents({
          offset: agents.length,
          limit: AGENT_LIST_PAGE_SIZE,
        }, includeEndOfWorkingAgents))
      }
    },
    [ agentPagination.next, agents.length, agentSearchValue, includeEndOfWorkingAgents, ]
  )

  const onAgentSelect = useCallback(
    (agent: Agent) => () => {
      if(agent.id.toString() !== selectedAgentId) {
        navigate(`/agents/${ agent.id }`)
      }

      if(map.current) {
        map.current.setZoom(14, false)
        map.current.panTo(new naver.maps.LatLng(
          agent.agent_location.coord.latitude,
          agent.agent_location.coord.longitude
        ), { duration: 500, })
      }
    },
    [ selectedAgentId, ]
  )

  return (
    <Container>
      <Header />
      <div className='map-container'>
        <DashboardUtils />
        <AgentListMap
          agents={ agents }
          selectedAgentId={ selectedAgentId }
          onAgentSelect={ onAgentSelect }
          map={ map.current }
          setMap={ (newMap) => map.current = newMap }
        />
        <AgentList
          agents={ agents }
          selectedAgentId={ selectedAgentId }
          onAgentSelect={ onAgentSelect }
          onEndReached={ onAgentsEndReached }
        />
        <ControlRoomRequestingSidebar />
        <AgentDetail agents={ agents } />
      </div>
    </Container>
  )
}

export default MainControllMap
